import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useJsonData } from '../../hooks/useJsonData';

interface ServiceItem {
  icon: string;
  title: string;
  description: string;
  slug: string;
}

interface ServicesData {
  services: ServiceItem[];
}

export function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const { data, loading } = useJsonData<ServicesData>('/data/services.json');

  const services = data?.services || [];

  return (
    <section ref={ref} id="services" className="py-20 lg:py-28 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4">
            Our Services
          </p>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            End-to-end waste solutions.
          </h2>
          <hr className="border-gray-200" />
        </motion.div>

        {/* Loading skeleton */}
        {loading && (
          <div className="space-y-0">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 border-b border-gray-100 animate-pulse bg-gray-50" />
            ))}
          </div>
        )}

        {/* Services list */}
        {!loading && (
          <div>
            {services.map((service, index) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 16 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.06 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className="group flex items-center gap-6 lg:gap-10 py-5 border-b border-gray-100 hover:bg-p-50 transition-colors px-2 -mx-2"
                >
                  {/* Number */}
                  <span className="text-3xl lg:text-4xl font-bold text-gray-200 group-hover:text-p-600 transition-colors w-14 shrink-0 tabular-nums">
                    {String(index + 1).padStart(2, '0')}
                  </span>

                  {/* Title */}
                  <span className="text-base lg:text-xl font-semibold text-gray-900 w-56 lg:w-72 shrink-0">
                    {service.title}
                  </span>

                  {/* Description */}
                  <span className="hidden md:block text-sm text-gray-500 flex-1 leading-relaxed">
                    {service.description}
                  </span>

                  {/* Arrow */}
                  <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-p-600 shrink-0 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* View all */}
        <div className="mt-10">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm font-medium text-p-600 hover:text-p-700 transition-colors"
          >
            View all services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
